import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useShopQuery } from "@hooks/use-shop-query.js";
import { gql } from "graphql-request";
import {getGidFromRestId} from "@utility/shopify";

const queryVariant = gql`
  query ProductAndCollection($country:CountryCode!, $variantId:ID!) @inContext(country: $country){
    cartPopups: metaobjects(first: 100, type: "cart_popup") {
      edges {
        node {
          name: field(key: "name") {
            value
          }
          page: field(key: "page") {
            value
            reference {
              ... on Page {
                handle
              }
            }
          }
          rules: field(key: "rules") {
            value
          }
          priority: field(key: "priority") {
            value
          }
          active: field(key: "active") {
            value
          }
        }
      }
    }
    node(id: $variantId) {
      ... on ProductVariant {
        title,
        id,
        sku,
        available: availableForSale,
        options: selectedOptions{
          name,
          value
        }
        price {
          amount
        }
        compareAtPrice {
          amount
        }
        parentCompatibilityKeys: metafield(namespace: "compatibility", key: "parent_keys") {
          value
        },
        includedItems: metafield(namespace: "included", key: "product_variants") {
          value,
          references(first: 8) {
            edges {
              node {
                ...on ProductVariant {
                  title
                  id
                  sku
                  image{
                    url
                    altText
                  }
                  price {
                    amount
                  }
                }
              }
            }
          }
        }
        product {
          title,
          cartNotes: metafields(identifiers: [
            {namespace: "cart-notes", key: "page"}
            {namespace: "cart-notes", key: "rules"}
            {namespace: "cart-notes", key: "priority"}
          ]) {
            key
            value
          }
          includedItemsTitle: metafield(namespace: "accentuate", key: "included_item_title") {
            value
          }
          customizerCollections: metafield(namespace: "customizer", key: "collections") {
            references(first: 10) {
              edges {
                node {
                  ... on Collection {
                    id
                    handle
                    title
                    descriptionHtml
                    customizerTitle: metafield(namespace: "customizer", key: "title") {
                      value
                    }
                    products(first: 100) {
                      edges {
                        node {
                          ...CustomizerItem
                        }
                      }
                    }
                    customizerExperiences: metafield(namespace: "customizer", key: "experiences") {
                      references(first: 10) {
                        edges {
                          node {
                            ... on Metaobject {
                              handle
                              title: field(key:"title"){
                                value
                              }
                              page: field(key:"page"){
                                reference {
                                  ... on Page {
                                    title
                                    handle
                                  }
                                }
                              }
                              compatibilityKeys: field(key:"compatibility_keys"){
                                value
                              }
                              live: field(key:"live"){
                                value
                              }
                              priority: field(key:"priority"){
                                value
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment CustomizerItem on Product {
		title,
		id,
		handle,
		available: availableForSale,
		description,
		title,
		type: productType,
		options{
			id,
			name,
			values
		}
		price: priceRange{
			maxVariantPrice {
				amount
			},
			minVariantPrice {
				amount
			}
		}
		image:featuredImage{
			url,
			altText
		}
		additionalOptions: metafield(namespace:"custom",key:"additional_product_options") {
			value
			references (first:100){
				edges {
					node {
						... on Metaobject {
							id,
							label: field(key:"label"){
								value
							}
							sublabel: field(key:"sublabel"){
								value
							}
							variant: field(key:"variant"){
								reference{
									... on ProductVariant{
										id
										compatibilityKeys: metafield(namespace:"compatibility",key:"keys") {
											value
										}
									}
								}
							}
							compatibility: field(key:"compatibility"){
								value
							}
						}
					}
				}
			}
		}
		usesVehiclePicker: metafield(namespace:"compatibility",key:"uses_vehicle_data") {
			value
		}
		variants(first:100){
			edges{
				node{
					id,
					title,
					sku,
					available: availableForSale,
					options: selectedOptions{
						name,
						value
					}
					price {
						amount
					}
					image {
						url,
						altText
					}
					compatibilityKeys: metafield(namespace:"compatibility",key:"keys") {
						value
					}
				}
			}
		}
	}`;

export const ProductCustomizerVariantContext = React.createContext({
  loading: true
});

export const ProductCustomizerVariantProvider = ({children, variantId, drawerElement}) => {

  const [focusedCollection, setFocusedCollection] = useState();
  const variables = { variantId: getGidFromRestId(variantId, "ProductVariant"), country: Shopify.country }
  const {data, loading:productLoading} = useShopQuery(queryVariant, variables);

  const toggleTrayOpen = useCallback((value) => {
    if (!drawerElement) return;
    const open = value !== undefined ? value : !drawerElement.hasAttribute('tray-open');
    open ? drawerElement.setAttribute('tray-open', '') : drawerElement.removeAttribute('tray-open');
  }, [drawerElement]);

  const variant = data?.node;

  if(variant){variant.includedItemsTitle = data?.product?.includedItemsTitle};

  const compatibilityKeyString = variant?.parentCompatibilityKeys?.value;

  const compatibilityKeys = useMemo(() => {
    if(!compatibilityKeyString) return undefined;
    try{
      return JSON.parse(compatibilityKeyString);
    }catch(e){
      console.error(e);
      console.error(`Unable to parse compatibilityKeys from ${variant.id}`);
    }
  }, [compatibilityKeyString]);

  const collections = useMemo(() => {
    if (!data?.node?.product) return [];
    const variantKeys = compatibilityKeys ?? [];
    return (data.node.product.customizerCollections?.references ?? []).map((collection) => {
      const experiences = collection.customizerExperiences?.references ?? [];
      const compatible = experiences.filter((experience) => {
        const expKeys = JSON.parse(experience.compatibilityKeys?.value ?? '[]');
        return expKeys.length === 0 || expKeys.some((key) => variantKeys.includes(key));
      });
      compatible.sort((a, b) => (Number(b.priority?.value) || 0) - (Number(a.priority?.value) || 0));
      return { ...collection, selectedExperience: compatible[0] ?? null };
    });
  }, [data, compatibilityKeys]);

  useEffect(() => {
    if (!collections.length || focusedCollection) return;
    const first = collections.find((c) => c.selectedExperience);
    if (first) setFocusedCollection(first.handle);
  }, [collections, focusedCollection]);

  const cartPopups = data?.cartPopups ?? [];

  const contextValue = useMemo(
    () => ({ loading:productLoading, variant, product: data?.node?.product, collections, compatibilityKeys, focusedCollection, setFocusedCollection, toggleTrayOpen, cartPopups }),
    [productLoading, variant, data, collections, compatibilityKeys, focusedCollection, setFocusedCollection, toggleTrayOpen, cartPopups]
  );

  return <ProductCustomizerVariantContext.Provider value={contextValue}>
    {children}
    </ProductCustomizerVariantContext.Provider>;
}
